import React, { useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { OfferFile } from "../../models/models";
import { ImagePreviewStrip, useGalleryImages } from "../../../../shared/components/gallery";
import offersApi from "../../api/offersApi";

interface OfferAttachmentsCardProps {
  files?: OfferFile[];
  offerId: string;
}

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;

const isImageFile = (file: OfferFile): boolean =>
  IMAGE_EXTENSIONS.test(file.name) || (file.content?.startsWith("data:image/") ?? false);

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const OfferAttachmentsCard: React.FC<OfferAttachmentsCardProps> = ({ files, offerId }) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  const imageFiles = useMemo(() => (files || []).filter(isImageFile), [files]);
  const nonImageFiles = useMemo(() => (files || []).filter((f) => !isImageFile(f)), [files]);
  const galleryImages = useGalleryImages({ type: "entityFiles", files: imageFiles });

  if (!files || files.length === 0) return null;

  const resolveFileContent = async (fileName: string): Promise<string> => {
    const fullOffer = await offersApi.getOffer(offerId);
    const file = fullOffer.files?.find((f) => f.name === fileName);
    if (!file?.content) return "";
    const content = file.content;
    if (content.startsWith("http://") || content.startsWith("https://") || content.startsWith("data:")) {
      return content;
    }
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    const mimeTypes: Record<string, string> = {
      pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
      gif: "image/gif", webp: "image/webp", doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    };
    const mime = mimeTypes[ext] || "application/octet-stream";
    return `data:${mime};base64,${content}`;
  };

  const handleFileView = async (e: React.MouseEvent, fileName: string) => {
    e.stopPropagation();
    if (downloadingFile) return;
    setDownloadingFile(fileName);
    try {
      const content = await resolveFileContent(fileName);
      if (!content) return;
      const link = document.createElement("a");
      link.href = content;
      link.target = "_blank";
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to view file:", err);
    } finally {
      setDownloadingFile(null);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radius,
        padding: "20px",
      }}
    >
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mb: "12px",
        }}
      >
        {translate("attachments")} ({files.length})
      </Box>

      {/* Image gallery */}
      {galleryImages.length > 0 && (
        <Box sx={{ mb: nonImageFiles.length > 0 ? "12px" : 0 }}>
          <ImagePreviewStrip images={galleryImages} maxVisible={4} />
        </Box>
      )}

      {/* Non-image files */}
      {nonImageFiles.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {nonImageFiles.map((file, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                transition: "all 0.15s ease",
                "&:hover": { borderColor: dt.blue, backgroundColor: dt.blueSoft },
              }}
            >
              {downloadingFile === file.name ? (
                <CircularProgress size={14} sx={{ color: dt.blue, flexShrink: 0 }} />
              ) : (
                <DescriptionOutlinedIcon sx={{ fontSize: 16, color: dt.textLink, flexShrink: 0 }} />
              )}
              <Box
                onClick={(e) => handleFileView(e, file.name)}
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: dt.textLink,
                  fontFamily: dt.font,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  flex: 1,
                  cursor: downloadingFile === file.name ? "wait" : "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {file.name}
              </Box>
              <Box sx={{ fontSize: "10px", color: dt.textMuted, fontFamily: dt.fontMono, flexShrink: 0 }}>
                {formatFileSize(file.size)}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OfferAttachmentsCard;
